/**
 * The small marks the viewer chrome needs, drawn rather than typed.
 *
 * The vendored Latin subsets of Source Serif 4 carry no U+2190/U+2192/U+2212
 * and none of the geometric shapes the design boards set in the toolbar, so a
 * literal character renders as tofu. Drawing them removes the dependency on
 * what a font subset happens to include, and the stroke follows `currentColor`
 * so each one inherits whatever it sits in.
 *
 * Chevrons are not here: `Chevron` in components/Page.tsx already draws those,
 * and one drawing of one mark is the rule.
 */

const PATHS = {
  search: 'M6.8 2.6a4.2 4.2 0 1 0 0 8.4 4.2 4.2 0 0 0 0-8.4M9.9 9.9 13.6 13.6',
  plus: 'M8 3.2V12.8M3.2 8H12.8',
  minus: 'M3.2 8H12.8',
  /** Expand: two corners pushed outward. */
  expand: 'M2.6 6.2V2.6h3.6M13.4 9.8v3.6H9.8M2.6 2.6 6.9 6.9M13.4 13.4 9.1 9.1',
  /** Fit: four corner brackets pointing in. */
  fit: 'M2.4 6V2.4h3.6M13.6 6V2.4H10M2.4 10v3.6h3.6M13.6 10v3.6H10',
  /** A jump: the story continues on another page. */
  jump: 'M5.2 10.8 11 5M6.4 5H11v4.6',
  check: 'M3.2 8.4 6.5 11.8 12.8 4.6',
  /** Swap: the two panes trade places. */
  swap: 'M2.6 5.6h10.8M11.2 3.4l2.2 2.2-2.2 2.2M13.4 10.4H2.6M4.8 8.2 2.6 10.4l2.2 2.2',
  close: 'M4 4l8 8M12 4l-8 8',
  /** A region selection: a marquee with a handle. */
  marquee: 'M2.6 5V2.6h2.4M11 2.6h2.4V5M13.4 11v2.4H11M5 13.4H2.6V11M6 6h4v4H6z',
  copy: 'M5.4 5.4V2.8h7.8v7.8h-2.6M2.8 5.4h7.8v7.8H2.8z',
} as const

export type GlyphName = keyof typeof PATHS

export function Glyph({
  name,
  size = 13,
  strokeWidth = 1.5,
}: {
  name: GlyphName
  size?: number
  strokeWidth?: number
}) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 16 16"
      fill="none"
      aria-hidden="true"
      style={{ flex: 'none', display: 'block' }}
    >
      <path
        d={PATHS[name]}
        stroke="currentColor"
        strokeWidth={strokeWidth}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  )
}
